import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DocumentAttachment, DocumentCategory } from '@/types/domain';
import { showToast } from '@/context/NotificationContext';
import { FileDropzone } from '../forms/FileDropzone';
import {
  FileText,
  Download,
  Trash2,
  ShieldCheck,
  AlertOctagon,
  Clock,
  Lock,
  Paperclip,
} from 'lucide-react';

interface DocumentManagementPanelProps {
  documents: DocumentAttachment[];
  onUploadFile: (file: File, category: DocumentCategory) => Promise<void>;
  onDownloadFile: (documentId: string) => Promise<void>;
  onDeleteFile: (documentId: string) => Promise<void>;
  disabled?: boolean;
}

export const DocumentManagementPanel: React.FC<DocumentManagementPanelProps> = ({
  documents,
  onUploadFile,
  onDownloadFile,
  onDeleteFile,
  disabled = false,
}) => {
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  const activeDocuments = documents.filter((d) => d.status !== 'DELETED');
  const totalAttachmentBytes = activeDocuments.reduce((acc, d) => acc + d.fileSize, 0);

  const handleDownload = async (docId: string) => {
    try {
      setDownloadingDocId(docId);
      await onDownloadFile(docId);
      showToast('Document Download Started', 'Attachment retrieved from secure vault.', 'success');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Download failed. Security or authorization restriction.';
      showToast('Download Restriction', msg, 'warning');
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingDocId) return;
    try {
      setIsDeleting(true);
      await onDeleteFile(deletingDocId);
      setDeletingDocId(null);
      showToast('Document Removed', 'Attachment removed from repository.', 'info');
    } catch {
      showToast('Delete Failed', 'Failed to delete document.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Card className="border border-border shadow-sm">
      <CardHeader className="pb-3 border-b border-border/50 flex flex-row items-center justify-between">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-primary" />
          <span>Attachments & Supporting Documents ({activeDocuments.length})</span>
        </CardTitle>
        <div className="text-right text-xs font-mono text-foreground-secondary">
          <span>Total Size: </span>
          <span className="font-bold text-foreground">
            {(totalAttachmentBytes / (1024 * 1024)).toFixed(2)} MB / 50 MB
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Upload Dropzone */}
        {!disabled && <FileDropzone onUploadFile={onUploadFile} disabled={disabled} />}

        {/* Document Datatable */}
        {activeDocuments.length === 0 ? (
          <div className="p-6 text-center text-xs text-foreground-secondary border border-dashed border-border rounded-lg">
            No supporting attachments uploaded for this procurement record.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-surface-hover/50 text-foreground-secondary border-b border-border text-left font-medium">
                  <th className="py-2.5 px-3">Document Name</th>
                  <th className="py-2.5 px-3">Category</th>
                  <th className="py-2.5 px-3 text-right">Size</th>
                  <th className="py-2.5 px-3">Uploaded By</th>
                  <th className="py-2.5 px-3 text-center">Scan Status</th>
                  <th className="py-2.5 px-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {activeDocuments.map((doc) => {
                  const isClean = doc.scanStatus === 'CLEAN' || doc.status === 'CLEAN';
                  const isInfected = doc.scanStatus === 'INFECTED' || doc.status === 'QUARANTINED';
                  const isScanning = doc.scanStatus === 'PENDING' || doc.status === 'SCANNING';

                  return (
                    <tr key={doc.id} className="hover:bg-surface-hover/30 transition-colors">
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-primary shrink-0" />
                          <span className="font-semibold text-foreground truncate max-w-[220px]" title={doc.fileName}>
                            {doc.fileName}
                          </span>
                        </div>
                      </td>

                      <td className="py-3 px-3">
                        <Badge variant="outline" className="text-[10px] py-0 font-mono">
                          {doc.category}
                        </Badge>
                      </td>

                      <td className="py-3 px-3 text-right font-mono text-foreground-secondary">
                        {(doc.fileSize / 1024).toFixed(0)} KB
                      </td>

                      <td className="py-3 px-3">
                        <div>
                          <p className="font-medium text-foreground">{doc.uploadedByName}</p>
                          <p className="text-[10px] text-foreground-secondary font-mono">
                            {new Date(doc.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </td>

                      <td className="py-3 px-3 text-center">
                        {isClean ? (
                          <Badge variant="success" className="text-[10px] py-0 gap-1 bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                            <ShieldCheck className="h-3 w-3" /> Clean
                          </Badge>
                        ) : isInfected ? (
                          <Badge variant="destructive" className="text-[10px] py-0 gap-1">
                            <AlertOctagon className="h-3 w-3" /> Quarantined
                          </Badge>
                        ) : isScanning ? (
                          <Badge variant="warning" className="text-[10px] py-0 gap-1">
                            <Clock className="h-3 w-3 animate-spin" /> Scanning
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px] py-0">
                            Unverified
                          </Badge>
                        )}
                      </td>

                      <td className="py-3 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            disabled={!isClean || downloadingDocId === doc.id}
                            onClick={() => handleDownload(doc.id)}
                            className="p-1 hover:bg-surface-hover rounded text-foreground-secondary hover:text-foreground disabled:opacity-30 cursor-pointer"
                            title={isClean ? 'Secure IDOR Download' : 'Download Blocked'}
                          >
                            {isClean ? <Download className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                          </button>
                          {!disabled && (
                            <button
                              type="button"
                              onClick={() => setDeletingDocId(doc.id)}
                              className="p-1 hover:bg-destructive/10 rounded text-destructive"
                              title="Delete Attachment"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingDocId && (
          <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-background border border-border rounded-xl shadow-2xl p-5 w-full max-w-md space-y-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertOctagon className="h-5 w-5" />
                <h3 className="font-bold text-sm">Confirm Attachment Deletion</h3>
              </div>
              <p className="text-xs text-foreground-secondary">
                Are you sure you want to delete this attachment? The record will be soft-deleted and logged in the audit trail.
              </p>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setDeletingDocId(null)} disabled={isDeleting}>
                  Cancel
                </Button>
                <Button variant="destructive" size="sm" isLoading={isDeleting} onClick={handleConfirmDelete}>
                  Delete Attachment
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
